import {
  Accordion,
  Box,
  Field,
  Flex,
  Heading,
  Link,
  Message,
  Modal,
  Text,
  toast,
} from '@workwhile/ui'
import { Check, ExternalLink, X } from 'lucide-react'
import React, { useMemo, useRef } from 'react'
import { useTheme } from 'styled-components'
import { useAssessRequirementsWorkflowContextValue } from '../context/AssessmentWorkflowProvider'
import { Transcript } from './Transcript'

const FeedbackController = ({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) => (
  <Field label="What did you think of this person?">
    <Box
      as="textarea"
      id="assessment-general-feedback"
      value={value}
      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
        onChange(e.target.value)
      }
      placeholder="Optional general feedback..."
      rows={4}
      width="100%"
      p={2}
      borderRadius="small"
      borderColor="lightGray"
      style={{
        resize: 'vertical',
        minHeight: 80,
        font: 'inherit',
        borderWidth: 1,
        borderStyle: 'solid',
      }}
    />
  </Field>
)

const EvaluationButton = ({
  variant = 'fail',
  onClick,
  isSelected = false,
}: {
  variant: 'pass' | 'fail'
  onClick: () => void
  isSelected?: boolean
}) => {
  const { colors } = useTheme()
  const config = useMemo(() => {
    if (!isSelected) {
      return {
        color: colors.lightText,
        backgroundColor: colors.white,
        border: `1px solid ${colors.lightText}`,
        boxShadow: `0 0 0 2px ${colors.offWhite}`,
      }
    }

    switch (variant) {
      case 'pass':
        return {
          color: colors.primary200,
          backgroundColor: colors.primary50,
          border: `1px solid ${colors.primary200}`,
          boxShadow: `0 0 0 2px ${colors.offWhite}`,
        }
      case 'fail':
        return {
          color: colors.error200,
          backgroundColor: colors.error50,
          border: `1px solid ${colors.error200}`,
        }
    }
  }, [isSelected])

  const Icon = useMemo(() => {
    switch (variant) {
      case 'pass':
        return Check
      case 'fail':
        return X
    }
  }, [variant])

  return (
    <Box
      borderRadius="100%"
      height={36}
      width={36}
      display="flex"
      alignItems="center"
      justifyContent="center"
      onClick={onClick}
      style={{
        cursor: 'pointer',
        backgroundColor: isSelected ? config?.backgroundColor : undefined,
        transition: 'background-color 0.2s',
      }}
      onMouseEnter={(e) => {
        ;(e.currentTarget as HTMLDivElement).style.backgroundColor =
          colors.offWhite
      }}
      onMouseLeave={(e) => {
        ;(e.currentTarget as HTMLDivElement).style.backgroundColor = isSelected
          ? config?.backgroundColor || ''
          : ''
      }}
      {...config}
    >
      <Icon absoluteStrokeWidth size={16} color={colors.error100} />
    </Box>
  )
}

export const AssessRequirementsWorkflowMain = () => {
  const { colors } = useTheme()
  const {
    state: {
      isOpen,
      data: { assessments, worker },
      formData = { assessments: {}, evaluationNote: '' },
      mutationState: { isPending, errorMessage: mutationErrorMessage },
    },
    actions: { submitFormToServer, setFormData, close },
  } = useAssessRequirementsWorkflowContextValue()

  const assessmentsData = formData.assessments

  const errorMessage = mutationErrorMessage

  const audioRefs = assessments.workerAssessment.interview.calls.map(() =>
    useRef<HTMLAudioElement | null>(null)
  )

  const handleSeekToTimestamp = (audioRef) => (targetSeconds: number) => {
    if (targetSeconds === null) {
      toast.error('Enter a timestamp like 90 or 01:30')
      return
    }

    if (!audioRef.current) {
      return
    }

    audioRef.current.currentTime = Math.max(0, targetSeconds)
    audioRef.current.play()
  }

  const handleEvaluationClick = (
    workerRequirementId: number,
    status: 'pass' | 'fail'
  ) => {
    setFormData((prev) => {
      const assessments = prev.assessments
      const currentStatus = assessments?.[workerRequirementId]?.status
      if (currentStatus === status) {
        const updated = { ...assessments }
        delete updated[workerRequirementId]
        return { ...prev, assessments: updated }
      }
      return {
        ...prev,
        assessments: {
          ...assessments,
          [workerRequirementId]: {
            workerRequirementId,
            status,
          },
        },
      }
    })
  }

  return (
    <Modal
      open={isOpen}
      onClose={close}
      position="top"
      loading={isPending}
      showCancel
      onOk={async () => {
        // No assessments submitted, close the workflow
        if (Object.keys(formData.assessments).length === 0) {
          close()
          return
        }

        try {
          await submitFormToServer()
          toast.success('Saved!')
        } catch (error) {
          toast.error('There was a problem saving your assessments.')
          console.error(error)
        }

        close()
      }}
      okText="Save"
      cancelText="Cancel"
    >
      <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
        {worker.name}
      </Heading>
      {!assessments.workerAssessment.interview ? (
        <Message variant="info" borderRadius="standard" mt={3}>
          Worker has not completed the assessment.
        </Message>
      ) : null}
      {assessments.workerAssessment.interview?.externalCandidateId ? (
        <>
          <Box mt={2}>
            <Link
              to={`https://app.classet.org/user/${assessments.workerAssessment.interview.externalCandidateId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View in Classet <ExternalLink size={14} color={colors.primary} />
            </Link>
          </Box>
          <Flex
            flex={1}
            flexDirection="column"
            gap={4}
            minHeight={0}
            overflow="hidden"
          >
            {assessments.workerAssessment.interview?.calls?.map(
              ({ audioUrl, transcriptUrl }, index) => {
                return (
                  <Flex
                    key={transcriptUrl + audioUrl}
                    flexDirection="column"
                    minHeight={0}
                    overflow="hidden"
                  >
                    {audioUrl ? (
                      <Box mt={3}>
                        <audio
                          onPlay={() => {
                            audioRefs.forEach((ref) => {
                              if (ref !== audioRefs[index] && ref.current) {
                                ref.current.pause()
                              }
                            })
                          }}
                          controls
                          style={{ width: '100%' }}
                          ref={audioRefs[index]}
                        >
                          <source src={audioUrl} type="audio/mpeg" />
                          Your browser does not support the audio element.
                        </audio>
                      </Box>
                    ) : null}
                    {transcriptUrl ? (
                      <Accordion
                        items={[
                          {
                            trigger: (
                              <Text color="primary" textAlign="right">
                                View Transcript
                              </Text>
                            ),
                            content: (
                              <Box
                                p={3}
                                height="400px"
                                overflowY="scroll"
                                borderRadius="standard"
                                borderColor={colors.lightGray}
                              >
                                <Transcript
                                  transcriptUrl={transcriptUrl}
                                  setSeekTime={handleSeekToTimestamp(
                                    audioRefs[index]
                                  )}
                                />
                              </Box>
                            ),
                          },
                        ]}
                      />
                    ) : null}
                  </Flex>
                )
              }
            )}

            <Flex flexDirection="column" mt={2}>
              {assessments.requirements.map((req, index) => {
                return (
                  <Flex
                    key={req.workerRequirementId}
                    backgroundColor={
                      index % 2 === 0 ? colors.offWhite : 'transparent'
                    }
                    borderRadius="standard"
                    flexDirection="row"
                    p={2}
                    gap={3}
                    justifyContent="space-between"
                  >
                    <Box flex={1}>
                      <Text variant="bodyMedium">{req.name}</Text>
                    </Box>
                    {assessments.workerAssessment.interview ? (
                      <Flex
                        flex={1}
                        justifyContent="flex-end"
                        gap={3}
                        alignItems="center"
                      >
                        {(['pass', 'fail'] as const).map((status) => (
                          <EvaluationButton
                            key={`${req.workerRequirementId}-${status}`}
                            variant={status}
                            isSelected={
                              assessmentsData?.[req.workerRequirementId]
                                ?.status === status
                            }
                            onClick={() =>
                              handleEvaluationClick(
                                req.workerRequirementId,
                                status
                              )
                            }
                          />
                        ))}
                      </Flex>
                    ) : null}
                    {errorMessage ? (
                      <Message variant="error" mt={3} borderRadius="small">
                        {errorMessage}
                      </Message>
                    ) : null}
                  </Flex>
                )
              })}
            </Flex>

            <Box mt={3}>
              <FeedbackController
                value={formData.evaluationNote ?? ''}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, evaluationNote: value }))
                }
              />
            </Box>
          </Flex>
        </>
      ) : null}
    </Modal>
  )
}
